import React from 'react';
import { useQuery } from '@tanstack/react-query';
import styles from '../TradeFlowTrackingTab.module.css';
import { getPools, PoolEntry } from '../../../services/tradeFlowAdminApi';

const POOLS_KEY = ['admin', 'trade-flow', 'pools'];

const ActivePoolSection: React.FC = () => {
  const { data: result, isLoading, isError, error, refetch } = useQuery({
    queryKey: POOLS_KEY,
    queryFn: getPools,
    refetchInterval: 15000, // auto-refresh every 15s
  });

  const active = result?.active ?? [];
  const reserve = result?.reserve ?? [];
  const totalActive = result?.total_active ?? 0;
  const totalReserve = result?.total_reserve ?? 0;

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>Active &amp; Reserve Pools</h2>
        <span className={styles.sectionBadge}>Tab 7</span>
        <button onClick={() => refetch()} className={styles.refreshButton} disabled={isLoading}>
          Refresh
        </button>
      </div>
      <p className={styles.sectionDescription}>
        Active Pool (top 10) is displayed to users for execution. Reserve Pool (top 5) provides
        immediate replacement when an Active entry is executed or rejected. Auto-refreshes every 15s.
      </p>

      {isError && <div className={styles.errorBanner}>{(error as Error)?.message || 'Failed to fetch pools'}</div>}

      {isLoading ? (
        <div className={styles.loadingState}>Loading pools...</div>
      ) : (
        <div className={styles.poolsGrid}>
          <div className={styles.poolCard}>
            <h3>Active Pool ({totalActive})</h3>
            {active.length === 0 ? (
              <div className={styles.emptyPool}>No active recommendations</div>
            ) : (
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Score</th>
                    <th>Confidence</th>
                    <th>Direction</th>
                  </tr>
                </thead>
                <tbody>
                  {active.map((entry) => (
                    <tr key={entry.recommendation_id}>
                      <td className={styles.mono}>{entry.symbol}</td>
                      <td className={styles.mono}>{entry.score.toFixed(0)}</td>
                      <td>
                        {entry.confidence !== null
                          ? `${(entry.confidence * 100).toFixed(1)}%`
                          : '-'}
                      </td>
                      <td>{entry.direction || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className={styles.poolCard}>
            <h3>Reserve Pool ({totalReserve})</h3>
            {reserve.length === 0 ? (
              <div className={styles.emptyPool}>No reserve recommendations</div>
            ) : (
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Score</th>
                    <th>Confidence</th>
                    <th>Direction</th>
                  </tr>
                </thead>
                <tbody>
                  {reserve.map((entry) => (
                    <tr key={entry.recommendation_id}>
                      <td className={styles.mono}>{entry.symbol}</td>
                      <td className={styles.mono}>{entry.score.toFixed(0)}</td>
                      <td>
                        {entry.confidence !== null
                          ? `${(entry.confidence * 100).toFixed(1)}%`
                          : '-'}
                      </td>
                      <td>{entry.direction || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivePoolSection;
